import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  FavoriteBorder,
  Logout,
  Menu,
  PersonOutline,
  Search as SearchIcon,
  VerifiedUser,
  Wallet,
  Settings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useConnections } from '../hooks/useConnections';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import HeaderProfileMenu from '../components/HeaderProfileMenu';
import '../styles/maandvo-dashboard.css';

const MAROON = '#7b0011';

const MyConnectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const mediaOrigin = String(api.defaults.baseURL || '').replace(/\/?api\/?$/, '');
  const {
    connections,
    loading,
    fetchConnections,
  } = useConnections();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const limit = 12;

  useEffect(() => {
    fetchConnections(page, limit).then((data) => {
      if (data) {
        setTotalPages(Math.ceil(data.total / limit));
      }
    });
  }, [page, fetchConnections]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => setPage(value);

  const getPhotoSrc = (photoUrl: string) => {
    if (!photoUrl) return '';
    if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
      return photoUrl;
    }
    if (photoUrl.startsWith('/')) {
      return `${mediaOrigin}${photoUrl}`;
    }
    return `${mediaOrigin}/${photoUrl}`;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const sidebarItems = useMemo(
    () => [
      { label: 'Dashboard', icon: <PersonOutline fontSize="small" />, action: () => navigate('/my-profile') },
      { label: 'Update Profile', icon: <PersonOutline fontSize="small" />, action: () => navigate('/my-profile?openCompleteProfile=1') },
      { label: 'Match Preferences', icon: <Settings fontSize="small" />, action: () => navigate('/my-profile?openMatchPreferences=1') },
      { label: 'Search Profiles', icon: <SearchIcon fontSize="small" />, action: () => navigate('/') },
      { label: 'Unlocked Profiles', icon: <FavoriteBorder fontSize="small" />, action: () => navigate('/my-connections'), active: true },
      { label: 'Recharge Account', icon: <Wallet fontSize="small" />, action: () => navigate('/wallet') },
      { label: 'Activation Status', icon: <VerifiedUser fontSize="small" />, action: () => navigate('/activation-status') },
      { label: 'Change Password', icon: <Settings fontSize="small" />, action: () => navigate('/change-password') },
      { label: 'Logout', icon: <Logout fontSize="small" />, action: handleLogout },
    ],
    [navigate]
  );

  const sidebarContent = (
    <Box className="maandvo-dashboard-sidebar" role="presentation">
      <Box className="maandvo-dashboard-brand">
        <Box component="img" src="/maandvo-logo.png" alt="Brand" />
      </Box>
      <Box className="maandvo-dashboard-nav">
        {sidebarItems.map((item) => (
          <Box key={item.label} className={`maandvo-dashboard-nav-item ${item.active ? 'active' : ''}`} onClick={item.action}>
            {item.icon}
            <span>{item.label}</span>
          </Box>
        ))}
      </Box>
      <Box className="maandvo-dashboard-premium">
        <Typography sx={{ color: '#ffd89a', fontWeight: 800, mb: 0.7 }}>Unlocked Profiles</Typography>
        <Typography sx={{ color: '#fff6ea', fontSize: '0.9rem', mb: 1.2 }}>
          View profiles whose contact details you have already unlocked.
        </Typography>
        <Button fullWidth variant="contained" sx={{ backgroundColor: '#ffb935', color: '#5d000d', fontWeight: 800 }} onClick={() => navigate('/browse-profiles')}>
          Browse Profiles
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box className="maandvo-dashboard">
      <Box className="maandvo-dashboard-mobile-top">
        <IconButton onClick={() => setMobileOpen(true)} sx={{ color: 'inherit' }}>
          <Menu />
        </IconButton>
        <Typography sx={{ fontWeight: 800 }}>Unlocked Profiles</Typography>
        <HeaderProfileMenu
          initial={user?.first_name?.[0] || 'U'}
          actions={[
            { label: 'Dashboard', onClick: () => navigate('/my-profile') },
            { label: 'Logout', onClick: handleLogout, icon: <Logout fontSize="small" />, color: '#b71c1c' },
          ]}
        />
      </Box>

      <Box className="maandvo-dashboard-layout">
        <Box className="maandvo-dashboard-sidebar desktop-only">{sidebarContent}</Box>

        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ display: { xs: 'block', md: 'none' } }}>
          <Box sx={{ width: 280 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <IconButton onClick={() => setMobileOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            {sidebarContent}
          </Box>
        </Drawer>

        <Box className="maandvo-dashboard-main">
          <Paper
            sx={{
              borderRadius: 4,
              border: '1px solid #ead4b5',
              background: 'linear-gradient(135deg, rgba(123, 0, 17, 0.95) 0%, rgba(158, 27, 45, 0.9) 70%, rgba(205, 156, 109, 0.82) 100%)',
              color: 'white',
              p: { xs: 2, md: 3 },
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' }, fontWeight: 900 }}>Unlocked Profiles</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.88)', mt: 0.7 }}>
              Profiles with unlocked contact details ({connections.length})
            </Typography>
          </Paper>

          <Paper
            sx={{
              borderRadius: 3,
              border: '1px solid #ead7be',
              backgroundColor: 'rgba(255,255,255,0.96)',
              p: { xs: 1.5, md: 2.2 },
            }}
          >
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress sx={{ color: MAROON }} />
              </Box>
            )}

            {!loading && connections.length === 0 && (
              <Alert severity="info" sx={{ mb: 1 }}>
                No unlocked profiles yet. Use Unlock Contact on a profile to add it here after payment.
              </Alert>
            )}

            {!loading && connections.length > 0 && (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
                    gap: 2,
                  }}
                >
                  {connections.map((connection) => {
                    const person = connection.target_user;
                    return (
                      <Box
                        key={connection.id}
                        sx={{
                          borderRadius: 3,
                          border: '1px solid #efdfc9',
                          overflow: 'hidden',
                          background: '#fffdfa',
                          boxShadow: '0 10px 22px rgba(69, 20, 8, 0.06)',
                        }}
                      >
                        <Box sx={{ height: 190, background: '#eed8bc' }}>
                          {person?.profile_photo_url ? (
                            <Box
                              component="img"
                              src={getPhotoSrc(person.profile_photo_url)}
                              alt={`${person.first_name} profile`}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : null}
                        </Box>

                        <Box sx={{ p: 1.6 }}>
                          <Typography sx={{ color: MAROON, fontWeight: 900, fontSize: '1.08rem' }}>
                            {person?.first_name} {person?.last_name}
                          </Typography>
                          <Typography sx={{ color: '#73645b', fontSize: '0.86rem', mt: 0.4 }}>
                            {person?.age ? `${person.age} years old` : 'Age not provided'}
                          </Typography>
                          <Typography sx={{ color: '#73645b', fontSize: '0.86rem', mt: 0.2 }}>
                            {person?.city || 'Location unavailable'}
                          </Typography>
                          <Typography sx={{ color: '#8a7c73', fontSize: '0.78rem', mt: 1 }}>
                            Unlocked {new Date(connection.updated_at).toLocaleDateString()}
                          </Typography>
                        </Box>

                        <Box sx={{ p: 1.2, pt: 0 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            sx={{ backgroundColor: MAROON, '&:hover': { backgroundColor: '#5f000e' }, fontWeight: 800 }}
                            onClick={() => navigate(`/profile/${person?.id}`)}
                          >
                            View Unlocked Profile
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5, gap: 1 }}>
                    {Array.from({ length: totalPages }).map((_, idx) => {
                      const pageNo = idx + 1;
                      return (
                        <Button
                          key={pageNo}
                          variant={pageNo === page ? 'contained' : 'outlined'}
                          onClick={(event) => handlePageChange(event as unknown as React.ChangeEvent<unknown>, pageNo)}
                          sx={{
                            minWidth: 42,
                            borderColor: MAROON,
                            color: pageNo === page ? 'white' : MAROON,
                            backgroundColor: pageNo === page ? MAROON : 'transparent',
                          }}
                        >
                          {pageNo}
                        </Button>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default MyConnectionsPage;
